<template>
  <transition
    name="modal"
    enter-class="modal--enter"
    enter-active-class="modal--enter-active"
    leave-class="modal--leave"
    leave-active-class="modal--leave-active"
  >
    <div v-if="active" class="modal" @keyup.esc="onClose">
      <div v-on-clickaway="onClose" class="modal__dialog">
        <EIconButton
          v-if="closeButton"
          icon="close"
          class="modal__close"
          data-test-id="close"
          @click.stop="onClose"
        />
        <div class="modal__content">
          <slot />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

import { focusFocusableChild } from '~~/common/js/client/keyboard-utils';
import EIconButton from '~~/common/vue/components/icon-button';

export default {
  name: 'EModal',
  components: {
    EIconButton,
  },
  mixins: [clickaway],
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    closeButton: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    active(value) {
      if (value) {
        // When the modal is shown, we want a fixed body
        // so the body won't scroll
        document.body.classList.add('no-scroll');
        this.$nextTick(() => focusFocusableChild(this.$el));
      } else {
        // When the modal is hidden,
        // we want to remain at the top of the scroll position
        document.body.classList.remove('no-scroll');
      }
    },
  },
  beforeDestroy() {
    // ensure the modal does not prevent page scrolling once it's destroyed
    document.body.classList.remove('no-scroll');
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
  },
};
</script>


<style lang="scss" scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 1;
  z-index: $z-index-overlay;
  background: $color-overlay-primary;
  transition: opacity $timing-s ease;
}

.modal__dialog {
  @include elevation(1);

  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
  padding: $space-3l;
  background: $color-background-primary;
  border-radius: $border-radius-s;

  @include mobile-l {
    width: 500px;
    height: auto;
    padding: 60px;
  }
}

.modal__content {
  max-height: 100%;
  width: 100%;
}

.modal__close {
  position: absolute;
  top: $space-m;
  right: $space-m;
}

.modal--enter,
.modal--leave-active {
  opacity: 0;
}
</style>

<docs>
A Modal

The modal expects the class `no-scroll` to be available on body.
This is needed to prevent the body from scrolling while the modal is open.

```css
body.no-scroll {
  position: absolute;
  overflow: hidden;
}
```

```js
let active = false;

<div>
  <EButton @click="active = true">Show modal</EButton>
  <EModal :active="active" @close="active = false">
    Hello
  </EModal>
</div>
```
</docs>
